import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import get from 'lodash.get';
import { FacebookShareButton } from 'react-share';

import { cleanApolloError } from '../../../../../util/normalize-errors';
import Button from '../../../../../components/global/Button';
import PhotoViewer from '../../../../../components/product/PhotoViewer';
import ContributorProfileBadge from '../../../../../components/global/ContributorProfileBadge';
import { getAttributionName } from '../../../../../util/account';
import { getThumbBySize } from '../../../../../util/photo';
import { getProductBasenames } from '../../../../../util/role-definitions';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import { ALBUM_TYPE } from '../../../../../constants/albums';
import DownloadPhotoButtonContainer from '../../../../../containers/global/DownloadPhotoButtonContainer';
import { PHOTO_ALBUM_FOLDER_TAGS } from '../../../../../constants/modals';
import PhotoAlbumFolderTagsModal from '../components/PhotoAlbumFolderTags/PhotoAlbumFolderTagsModal';
import PhotoForFacesHighlightContainer from '../../../components/FaceHighlightsPhotoGrid/PhotoForFacesHighlight';
import CoverPhotoButton from './CoverPhotoButton';
import { NoContributorPermissionBlock } from '../../../services/contributors';
import ReportPhotoButton from '../../../../../components/global/ReportPhotoButton/ReportPhotoButton';
import FaceTagPhoto from '../../FaceTagPhoto/FaceTagPhoto';
import SpotlightIcon from '../../../../../components/icons/SpotlightIcon';
import DropdownMenu from '../../../../../components/global/DropdownMenu';
import DownloadIcon from '../../../../../components/icons/DownloadIcon';
import AlbumsIcon from '../../../../../components/icons/AlbumsIcon';
import WarningIcon from '../../../../../components/icons/WarningIcon';
import TrashIcon from '../../../../../components/icons/TrashIcon';
import {
  canAccessSpotlightOnBasename,
  canReportPhotos,
} from '../../../../../util/product-permissions';
import FacebookCircleIcon from '../../../../../components/icons/FacebookCircleIcon';
import getConfig from '../../../../../util/config';
import { removeHashes } from '../../../../../util/text';
import FeatureBlock from '../../../../../components/contextual/FeatureBlock';
import Angle from '../../../../../components/global/Angle';

class AlbumPhotoViewer extends Component {
  static propTypes = {
    basename: PropTypes.oneOf(getProductBasenames()),
    albumId: PropTypes.string,
    photoId: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        watermarked_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
      }),
    ).isRequired,
    singlePhoto: PropTypes.shape({
      id: PropTypes.string,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      ),
      watermarked_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      ),
    }),
    deletePhoto: PropTypes.func,
    navigationPath: PropTypes.string,
    hideActions: PropTypes.bool,
    photoActions: PropTypes.func,
    preferWatermarked: PropTypes.bool,
    maxSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    includeLinkInActions: PropTypes.bool,
    actionClassName: PropTypes.string,
    forceImageSize: PropTypes.bool,
    history: PropTypes.shape({
      replace: PropTypes.func,
      length: PropTypes.number,
      goBack: PropTypes.func,
    }),
    backButtonLabel: PropTypes.string,
    loadMore: PropTypes.func,
    totalPhotos: PropTypes.number,
    accessor: PropTypes.string,
    openModal: PropTypes.func,
    forceNavigationPath: PropTypes.bool,
    showAlbumLink: PropTypes.bool,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    className: PropTypes.string,
    useBlurredBackground: PropTypes.bool,
  };

  static defaultProps = {
    albumId: '',
    photoId: '',
    photos: [],
    includeLinkInActions: false,
    backButtonLabel: 'Back',
    forceNavigationPath: false,
  };

  state = {
    highlightsModeEnabled: false,
    faceTagModeEnabled: false,
  };

  getAlbumId = currentPhotoItem => {
    const { albumId, album } = this.props;
    return albumId || album?.id || currentPhotoItem?.album?.id;
  };

  onPhotoDelete = async () => {
    const { deletePhoto, photoId, history, basename } = this.props;

    // eslint-disable-next-line
    this.setState(() => ({ deletePhotoError: '' }));

    try {
      await deletePhoto(photoId);
      history.replace(`/app/${basename}/albums/${this.getAlbumId()}/photos`);
    } catch ({ message }) {
      // TODO: Handle this error with a global error alert toast.
      // eslint-disable-next-line
      this.setState(() => ({ deletePhotoError: cleanApolloError(message) }));
    }
  };

  getNavigationPath = () => {
    const { basename, navigationPath } = this.props;

    return (
      navigationPath || `/app/${basename}/albums/${this.getAlbumId()}/photos`
    );
  };

  /**
   * If the user has a history take them back to where they were
   * which is the photo viewer via history.goBack() so that they
   * don't lose scroll position (imagine scroll though a lot of photos).
   * If they don't have a history (i.e. Direct URL access) navigate
   * to the photos navigation path.
   *
   * @returns {React.Component|Link}
   */
  renderBackButton = () => {
    const { history, backButtonLabel, forceNavigationPath } = this.props;
    const path = this.getNavigationPath();

    return history.length > 1 && !forceNavigationPath ? (
      <a
        href="#"
        className="photo-viewer-return"
        onClick={event => {
          event.preventDefault();
          history.goBack();
        }}
      >
        <Angle direction="left" className="mr-1" />
        {backButtonLabel}
      </a>
    ) : (
      <Link className="photo-viewer-return" to={path}>
        <Angle direction="left" className="mr-1" /> {backButtonLabel}
      </Link>
    );
  };

  renderPhotoForHighlights = (photoRecord, props) => {
    const { highlightsModeEnabled, faceTagModeEnabled } = this.state;

    return (
      <Fragment>
        {highlightsModeEnabled && (
          <PhotoForFacesHighlightContainer
            {...props}
            albumId={this.getAlbumId()}
            photoRecord={photoRecord}
            onClose={() => {
              this.setState(() => ({ highlightsModeEnabled: false }));
            }}
          />
        )}
        {faceTagModeEnabled && (
          <FaceTagPhoto
            albumId={this.getAlbumId()}
            photoId={photoRecord.id}
            onClose={() => {
              this.setState(() => ({ faceTagModeEnabled: false }));
            }}
          />
        )}
      </Fragment>
    );
  };

  render() {
    const {
      photos,
      photoId,
      hideActions,
      photoActions,
      preferWatermarked,
      maxSize,
      includeLinkInActions,
      actionClassName,
      forceImageSize,
      history,
      singlePhoto,
      loadMore,
      totalPhotos,
      accessor,
      openModal,
      deletePhoto,
      basename,
      album,
      showAlbumLink,
      className,
      useBlurredBackground,
    } = this.props;

    const path = this.getNavigationPath();
    const link = this.renderBackButton();

    const actionClasses = classNames({
      'photo-viewer-actions': true,
      'photo-viewer-actions-with-link': includeLinkInActions,
      [actionClassName]: !!actionClassName,
    });

    const availablePhotos =
      singlePhoto && !photos.map(photo => photo.id).includes(singlePhoto.id)
        ? [singlePhoto]
        : photos;

    const { highlightsModeEnabled, faceTagModeEnabled } = this.state;
    const currentPhotoItem = photos.find(({ id }) => id === photoId);

    const photoRecord = accessor
      ? get(currentPhotoItem, accessor)
      : currentPhotoItem;

    const displayName = getAttributionName(photoRecord?.attribution);
    const contributorDisplayUrl = photoRecord?.attribution?.avatar_photo
      ? getThumbBySize(
          photoRecord.attribution.avatar_photo.thumbnail_urls,
          IMAGE_SIZE_CODE.SMALL,
        ).url
      : undefined;

    const showContributorBadge =
      displayName && album?.album_type === ALBUM_TYPE.SHUTTERBUG;

    const hashCode = removeHashes(album?.hash_code);

    return photos.length ? (
      <PhotoViewer
        className={className}
        photos={availablePhotos}
        photoId={photoId}
        navigationPath={path}
        preferWatermarked={preferWatermarked}
        maxSize={maxSize}
        forceImageSize={forceImageSize}
        history={history}
        loadMore={loadMore}
        totalPhotos={totalPhotos}
        accessor={accessor}
        renderPhoto={
          !hideActions &&
          !photoActions &&
          (highlightsModeEnabled || faceTagModeEnabled)
            ? this.renderPhotoForHighlights
            : undefined
        }
        useBlurredBackground={useBlurredBackground}
      >
        <header className="photo-viewer-header">
          {!includeLinkInActions && link}

          {!hideActions && !photoActions && (
            <div className={actionClasses}>
              {includeLinkInActions && link}

              <NoContributorPermissionBlock entityId={this.getAlbumId()}>
                <Button
                  btnStyle={faceTagModeEnabled ? 'yellow' : 'glass'}
                  btnSize="sm"
                  className="ml-1"
                  onClick={() =>
                    this.setState(() => ({
                      faceTagModeEnabled: !faceTagModeEnabled,
                    }))
                  }
                >
                  Face Tag
                </Button>
                <FeatureBlock
                  condition={canAccessSpotlightOnBasename(basename)}
                >
                  <Button
                    btnStyle="glass"
                    btnSize="sm"
                    className="ml-1"
                    onClick={() =>
                      this.setState(() => ({
                        highlightsModeEnabled: !highlightsModeEnabled,
                      }))
                    }
                  >
                    <SpotlightIcon color="#fff" className="mr-1" /> Spotlight
                  </Button>
                </FeatureBlock>
                {openModal &&
                  !!(
                    album?.album_folder?.id ?? album?.root_album_folder?.id
                  ) && (
                    <Fragment>
                      <Button
                        btnStyle="glass"
                        btnSize="sm"
                        className="ml-1"
                        onClick={() =>
                          openModal(PHOTO_ALBUM_FOLDER_TAGS, {
                            photoId: photoRecord?.id,
                            albumId: album?.id,
                          })
                        }
                      >
                        Tags
                      </Button>

                      <PhotoAlbumFolderTagsModal />
                    </Fragment>
                  )}
              </NoContributorPermissionBlock>
              <DropdownMenu
                toggle={
                  <Button
                    btnStyle="glass"
                    btnSize="sm"
                    className="ml-1"
                    btnBlock
                  >
                    &hellip;
                  </Button>
                }
                positionFixed
                toggleClassName="mr-1"
                dropdownClassName="p-2"
              >
                {photoRecord && (
                  <DownloadPhotoButtonContainer
                    photoId={get(photoRecord, 'id')}
                    photoSize={IMAGE_SIZE_CODE.LARGE2X}
                    includeWatermark={false}
                    btnStyle="link"
                    btnSize="sm"
                    btnBlock
                    className="text-color-base d-flex align-items-center justify-content-start"
                  >
                    <DownloadIcon className="mr-1" size={15} color="#646e71" />{' '}
                    Download photo
                  </DownloadPhotoButtonContainer>
                )}

                <FacebookShareButton
                  className="btn btn-sm btn-link text-color-base d-flex align-items-center justify-content-start"
                  url={`${getConfig().app.baseUrl}/p/${photoRecord?.id}`}
                  hashtag={album?.hash_code && `#${hashCode}`}
                >
                  <FacebookCircleIcon
                    color="#646e71"
                    width="20"
                    className="mr-1"
                  />
                  <span className="d-none d-md-inline-block">Share</span>
                </FacebookShareButton>

                <NoContributorPermissionBlock entityId={this.getAlbumId()}>
                  <CoverPhotoButton
                    btnStyle="link"
                    btnSize="sm"
                    photoId={photoId}
                    btnBlock
                    className="text-color-base d-flex align-items-center justify-content-start"
                  >
                    <AlbumsIcon className="mr-1" size={15} color="#646e71" />
                    Use as cover photo
                  </CoverPhotoButton>

                  {(showAlbumLink || deletePhoto) && <hr className="my-1" />}

                  {showAlbumLink && (
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      to={`/app/${basename}/albums/${get(album, 'id')}/photos`}
                      btnBlock
                      className="text-color-base d-flex align-items-center justify-content-start"
                    >
                      {get(album, 'name') || 'Go to Album'}
                    </Button>
                  )}
                  {deletePhoto && (
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      onClick={this.onPhotoDelete}
                      btnBlock
                      className="text-color-base d-flex align-items-center justify-content-start"
                    >
                      <TrashIcon size={15} className="mr-1" color="#646e71" />
                      Delete
                    </Button>
                  )}
                </NoContributorPermissionBlock>

                <hr className="my-1" />

                {canReportPhotos(album) && (
                  <ReportPhotoButton
                    btnStyle="link"
                    btnSize="sm"
                    albumId={this.getAlbumId()}
                    photoId={photoId}
                    btnBlock
                    className="text-color-base d-flex align-items-center justify-content-start"
                  >
                    <WarningIcon size="15" className="mr-1" color="#646e71" />
                    Report photo
                  </ReportPhotoButton>
                )}
              </DropdownMenu>
            </div>
          )}

          {!hideActions && photoActions && (
            <div className={actionClasses}>
              {includeLinkInActions && link}
              {photoActions(photos.find(photo => photo.id === photoId))}
            </div>
          )}
        </header>
        {showContributorBadge && (
          <ContributorProfileBadge
            displayName={displayName}
            displayPhotoSrc={contributorDisplayUrl}
            prependText="Photo by"
            className="photo-attribution light"
            accountId={photoRecord?.attribution?.account_id}
            photoAttributionPageEnabled={
              photoRecord?.attribution?.photo_attribution_page?.is_enabled
            }
          />
        )}
      </PhotoViewer>
    ) : null;
  }
}

export default AlbumPhotoViewer;
